import { useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Select } from '@factorialco/f0-react'
import { EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { Delete, Plus } from '@factorialco/f0-react/icons/app'
import { WorkflowTeamSelectorDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

type CriteriaSpec = {
  criterias: {
    criteria: 'anyone_in_team' | 'leads_in_team'
    input: { team_id: string }
  }
}

type WhitelistSpec = {
  whitelist: string[]
}

type PeopleGroupSpec = CriteriaSpec | WhitelistSpec

const isCriteriaSpec = (spec: PeopleGroupSpec): spec is CriteriaSpec => 'criterias' in spec
const isWhitelistSpec = (spec: PeopleGroupSpec): spec is WhitelistSpec => 'whitelist' in spec

type CriteriaType = 'anyone_in_team' | 'leads_in_team' | 'whitelist'

const getCriteriaOptions = (): Array<{ value: CriteriaType; label: string }> => [
  { value: 'anyone_in_team', label: i18n.t('procurement.people_group_specs_editor.team_members') },
  { value: 'leads_in_team', label: i18n.t('procurement.people_group_specs_editor.team_leads') },
  { value: 'whitelist', label: i18n.t('procurement.people_group_specs_editor.specific_employees') },
]

const SpecLabel = ({
  spec,
  teams,
}: {
  spec: PeopleGroupSpec
  teams: Array<{ id: string; name: string }>
}) => {
  if (isCriteriaSpec(spec)) {
    const teamName =
      teams.find((t) => t.id === spec.criterias.input.team_id)?.name ??
      i18n.t('procurement.people_group_specs_editor.unknown')
    const label =
      spec.criterias.criteria === 'anyone_in_team'
        ? i18n.t('procurement.people_group_specs_editor.team_members_of', { team_name: teamName })
        : i18n.t('procurement.people_group_specs_editor.team_leads_of', { team_name: teamName })
    return <Text size="200">{label}</Text>
  }

  if (isWhitelistSpec(spec)) {
    const count = spec.whitelist.length
    return (
      <Text size="200">
        {i18n.t('procurement.people_group_specs_editor.specific_employees_count', { count })}
      </Text>
    )
  }

  return null
}

const AddSpecForm = ({
  onSave,
  onCancel,
}: {
  onSave: (spec: PeopleGroupSpec) => void
  onCancel: () => void
}) => {
  const [criteriaType, setCriteriaType] = useState<CriteriaType | null>(null)
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([])

  const { data, loading: teamsLoading } = useQuery(WorkflowTeamSelectorDocument)
  const availableTeams = (data?.teams?.teamsConnection?.edges || []).map((edge) => edge.node)

  const handleSave = () => {
    if (criteriaType === 'anyone_in_team' || criteriaType === 'leads_in_team') {
      if (!selectedTeamId) return
      onSave({
        criterias: {
          criteria: criteriaType,
          input: { team_id: selectedTeamId },
        },
      })
    } else if (criteriaType === 'whitelist') {
      if (selectedEmployeeIds.length === 0) return
      onSave({ whitelist: selectedEmployeeIds })
    }
  }

  const canSave =
    (criteriaType === 'anyone_in_team' && selectedTeamId !== null) ||
    (criteriaType === 'leads_in_team' && selectedTeamId !== null) ||
    (criteriaType === 'whitelist' && selectedEmployeeIds.length > 0)

  const selectedTeam = selectedTeamId
    ? availableTeams.find((team) => team.id === selectedTeamId)
    : undefined
  const selectedTeamEntities = selectedTeam
    ? [{ id: selectedTeam.id, name: selectedTeam.name, avatar: buildUrl(selectedTeam.avatar) }]
    : []

  return (
    <Box
      gap="s8"
      padding="s8"
      border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
      borderRadius={{ all: 'abs008' }}
    >
      <F0Select
        size="md"
        hideLabel
        clearable={false}
        label={i18n.t('procurement.people_group_specs_editor.criteria_type')}
        placeholder={i18n.t('procurement.people_group_specs_editor.select_criteria')}
        options={getCriteriaOptions()}
        value={criteriaType}
        onChange={(value: CriteriaType) => {
          setCriteriaType(value)
          setSelectedTeamId(null)
          setSelectedEmployeeIds([])
        }}
      />

      {(criteriaType === 'anyone_in_team' || criteriaType === 'leads_in_team') && (
        <EntitySelect
          label={i18n.t('procurement.people_group_specs_editor.team')}
          hideLabel
          loading={teamsLoading}
          entities={availableTeams.map(({ id, avatar, name }) => ({
            avatar: buildUrl(avatar),
            expanded: false,
            id,
            name,
          }))}
          notFoundSubtitle={i18n.t('procurement.people_group_specs_editor.no_teams_found')}
          notFoundTitle={i18n.t('procurement.people_group_specs_editor.no_teams')}
          onGroupChange={() => {}}
          onItemExpandedChange={() => {}}
          onSelect={(selection: EntitySelectEntity | null) => {
            setSelectedTeamId(selection ? String(selection.id) : null)
          }}
          searchPlaceholder={i18n.t('procurement.people_group_specs_editor.search_teams')}
          selectedEntities={selectedTeamEntities}
          selectedLabel={i18n.t('procurement.people_group_specs_editor.selected_team')}
          singleSelector
          size="md"
          placeholder={i18n.t('procurement.people_group_specs_editor.select_a_team')}
          selectedItemsCopy={i18n.t('procurement.people_group_specs_editor.team_selected')}
          groups={[]}
          selectedGroup="all"
          clearLabel={i18n.t('procurement.people_group_specs_editor.clear')}
          selectAllLabel={i18n.t('procurement.people_group_specs_editor.select_all')}
        />
      )}

      {criteriaType === 'whitelist' && (
        <EmployeeSelectorV2
          multiple
          options={{ disableSelectAll: true, hideLabel: true, size: 'md' }}
          onChange={(state) => {
            if (state.type === 'manual') {
              setSelectedEmployeeIds(state.ids)
            }
          }}
          value={selectedEmployeeIds}
        />
      )}

      <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
        <F0Button
          label={i18n.t('procurement.people_group_specs_editor.cancel')}
          variant="ghost"
          size="sm"
          onClick={onCancel}
        />
        <F0Button
          label={i18n.t('procurement.people_group_specs_editor.save')}
          variant="default"
          size="sm"
          onClick={handleSave}
          disabled={!canSave}
        />
      </Box>
    </Box>
  )
}

export const PeopleGroupSpecsEditor = ({
  value,
  onChange,
  error,
}: {
  value: string
  onChange: (v: string) => void
  error?: string | boolean
}) => {
  const [isAdding, setIsAdding] = useState(false)

  const specs: PeopleGroupSpec[] = useMemo(() => {
    try {
      const parsed: PeopleGroupSpec[] = JSON.parse(value)
      return parsed
    } catch {
      return []
    }
  }, [value])

  const { data } = useQuery(WorkflowTeamSelectorDocument)
  const availableTeams = (data?.teams?.teamsConnection?.edges || []).map((edge) => ({
    id: edge.node.id,
    name: edge.node.name,
  }))

  const handleDelete = (index: number) => {
    const newSpecs = specs.filter((_, i) => i !== index)
    onChange(JSON.stringify(newSpecs))
  }

  const handleAdd = (spec: PeopleGroupSpec) => {
    const newSpecs = [...specs, spec]
    onChange(JSON.stringify(newSpecs))
    setIsAdding(false)
  }

  return (
    <Box gap="s8">
      <Text size="100" weight="medium" color="textSecondary">
        {i18n.t('procurement.people_group_specs_editor.groups')}
      </Text>

      {specs.map((spec, index) => (
        <Box
          key={`spec-${index}`}
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
          padding="s8"
          border={{ all: { width: 's1', color: 'grey300', style: 'solid' } }}
          borderRadius={{ all: 'abs008' }}
        >
          <SpecLabel spec={spec} teams={availableTeams} />
          <F0Button
            icon={Delete}
            label={i18n.t('common.delete')}
            hideLabel
            onClick={() => handleDelete(index)}
            size="sm"
            variant="critical"
          />
        </Box>
      ))}

      {isAdding ? (
        <AddSpecForm onSave={handleAdd} onCancel={() => setIsAdding(false)} />
      ) : (
        <Box flexDirection="row">
          <F0Button
            label={i18n.t('procurement.people_group_specs_editor.add_group')}
            icon={Plus}
            variant="ghost"
            size="sm"
            onClick={() => setIsAdding(true)}
          />
        </Box>
      )}

      {error && typeof error === 'string' && (
        <Text size="100" color="textCritical">
          {error}
        </Text>
      )}
    </Box>
  )
}
